import { Box, CircularProgress, Divider, Paper, PaperProps, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Action, ScenarioFragmentFragment, useAdminScenarioQuery, useScenarioQuery } from '../../api/gql';
import { DataEntry } from '../../components/DataEntry';
import { ActionIcon } from '../../components/invoice/ScheduledActionIcon';
import { ActionName } from '../../components/invoice/ScheduledActionName';
import { Tabs } from '../../components/tabs';
import { scenarioPlanToTransMap } from '../../utils/maps';

type ScenarioInfoBoxProps = {
  scenarioId: string;
} & PaperProps;

function ScenarioInfoBoxComponent({ data, sx, ...rest }: { data: ScenarioFragmentFragment } & PaperProps) {
  const { t } = useTranslation('universal-reminding', {
    keyPrefix: 'scenarios',
  });
  return (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        ...sx,
      }}
      {...rest}
    >
      <Stack gap={1}>
        <DataEntry title={t('form.name')} text={data?.name} />
        <DataEntry title={t('form.plan')} text={data?.plan.name ? t(scenarioPlanToTransMap[data?.plan.name]) : ''} />
      </Stack>
      <Box flex={1}>
        <Stack direction={'row'} spacing={0.5}>
          <ActionIcon action={Action.Email} fontSize={'small'} color={'action'} />
          <ActionName action={Action.Email} fontSize={'small'} />
        </Stack>
        <Divider sx={{ my: 1 }} />
        <Box p={2}>
          <Tabs
            items={data.textEmail.map((email, index) => (
              <Stack key={index} gap={1}>
                <DataEntry title={t('form.subject')} text={data?.subject[index]} />
                <DataEntry title={t('form.emailBody')} text={email} />
              </Stack>
            ))}
            getTabName={(index) => (
              <Typography fontSize={'small'}>
                {`${index + 1}.`} <ActionName component={'span'} action={Action.Email} fontSize={'inherit'} />
              </Typography>
            )}
          />
        </Box>
      </Box>
      <Box>
        <Stack direction={'row'} spacing={0.5}>
          <ActionIcon action={Action.Sms} fontSize={'small'} color={'action'} />
          <ActionName action={Action.Sms} fontSize={'small'} />
        </Stack>
        <Divider sx={{ my: 1 }} />
        <Box p={2}>
          <Tabs
            items={data.textSms.map((sms, index) => (
              <DataEntry key={index} title={t('form.smsBody')} text={sms} />
            ))}
            getTabName={(index) => (
              <Typography fontSize={'small'}>
                {`${index + 1}.`} <ActionName component={'span'} action={Action.Sms} fontSize={'inherit'} />
              </Typography>
            )}
          />
        </Box>
      </Box>
      <Box>
        <Stack direction={'row'} spacing={0.5}>
          <ActionIcon action={Action.Call} fontSize={'small'} color={'action'} />
          <ActionName action={Action.Call} fontSize={'small'} />
        </Stack>
        <Divider sx={{ my: 1 }} />
        <Box p={2}>
          <Tabs
            items={data.textCall.map((call, index) => (
              <DataEntry key={index} title={t('form.callBody')} text={call} />
            ))}
            getTabName={(index) => (
              <Typography fontSize={'small'}>
                {`${index + 1}.`} <ActionName component={'span'} action={Action.Call} fontSize={'inherit'} />
              </Typography>
            )}
          />
        </Box>

        <Typography variant={'caption'} color={'text.secondary'}>
          {t('form.callHelperText')}
        </Typography>
      </Box>
    </Paper>
  );
}

export const ScenarioInfoBox = ({ scenarioId, sx, ...rest }: ScenarioInfoBoxProps) => {
  const { data, isLoading, isError } = useScenarioQuery({ id: scenarioId });

  if (isLoading) {
    return <CircularProgress />;
  }

  if (isError) {
    return <div>Error</div>;
  }

  return data?.scenario ? <ScenarioInfoBoxComponent sx={sx} data={data.scenario} {...rest} /> : null;
};

export const AdminScenarioInfoBox = ({ scenarioId, sx, ...rest }: ScenarioInfoBoxProps) => {
  const { data, isLoading, isError } = useAdminScenarioQuery({
    id: scenarioId,
  });

  if (isLoading) {
    return <CircularProgress />;
  }

  if (isError) {
    return <div>Error</div>;
  }

  return data?.adminScenario ? <ScenarioInfoBoxComponent sx={sx} data={data.adminScenario} {...rest} /> : null;
};
